import { inject, Injectable } from '@angular/core';
import { doc, docData, Firestore, updateDoc } from '@angular/fire/firestore';
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { map, Observable, of, switchMap } from 'rxjs';

import { AdvancedSettingsProperties, PaymentInfo, UserDetail } from './user-detail';
import { AuthService } from './auth.service';
import { User } from './user';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly authService = inject(AuthService);
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly user$: Observable<User | null>;

  constructor() {
    this.user$ = this.authService.authState$.pipe(
      switchMap((user) => {
        if (!user) {
          return of(null);
        }

        const userDocRef = doc(this.firestore, `users/${user.uid}`);
        return docData(userDocRef, { idField: 'uid' }) as Observable<User>;
      })
    );
  }

  getUser$(): Observable<User | undefined | null> {
    return this.user$;
  }

  getUid(): Observable<string | null> {
    return this.authService.authState$.pipe(map((user) => user?.uid ?? null));
  }

  updateUser(uid: string, userData: UserDetail): Promise<void> {
    return updateDoc(doc(this.firestore, `users/${uid}`), {
      name: userData.name,
      surname: userData.surname,
      company: userData.company,
      contactPersonFirstName: userData.contactPersonFirstName,
      contactPersonLastName: userData.contactPersonLastName,
      contactPersonEmail: userData.contactPersonEmail,
    });
  }

  updateUserPaymentInfo(uid: string, paymentInfo: PaymentInfo): Promise<void> {
    return updateDoc(doc(this.firestore, `users/${uid}`), {
      paymentInfo: paymentInfo,
    });
  }

  updateAdvancedSettings(uid: string, advancedSettings: AdvancedSettingsProperties): Promise<void> {
    return updateDoc(doc(this.firestore, `users/${uid}`), {
      advancedSettings: advancedSettings,
    });
  }

  setInitialSetupProperty(uid: string, property: string, value: boolean): Observable<void> {
    return httpsCallableData<{ uid: string; property: string; value: boolean }, void>(
      this.functions,
      'setInitialSetupPropertyStatus'
    )({ uid, property, value });
  }

  checkInitialSetupComplete(): Observable<boolean> {
    return this.user$.pipe(
      map(
        (user) =>
          !!user &&
          !!user.initialSetup &&
          user.initialSetup.domainVerified &&
          user.initialSetup.profileUpdated &&
          user.initialSetup.whitelistingSet
        // user.initialSetup.paymentInfoProvided
      )
    );
  }

  getUserAnonimitySetting(): Observable<boolean> {
    return this.user$.pipe(map((user) => user?.advancedSettings?.anonimity || true));
  }

  getListenerUrls(): Observable<string[]> {
    return this.user$.pipe(map((user) => user?.userConfig?.url || []));
  }
}
